import { Perspective } from "@/registration-tools/common/store/registration-datatypes";
import {
  FaroTooltip,
  ToolButton,
  ToolGroup,
  Toolbar,
  ViewPredefinedFrontIcon,
  ViewPredefinedRightIcon,
  ViewPredefinedTopIcon,
} from "@faro-lotv/flat-ui";
import { MouseEvent, useCallback, type JSX } from "react";

type PerspectiveSwitchProps = {
  /** The currently active perspective. */
  perspective: Perspective;

  /** A callback to execute when the user changes the perspective. */
  onPerspectiveChange(perspective: Perspective): void;
};

/** @returns A toolbar to change the 2D perspective. */
export function PerspectiveSwitch({
  perspective,
  onPerspectiveChange,
}: PerspectiveSwitchProps): JSX.Element {
  const changePerspective = useCallback(
    (_event: MouseEvent, value: Perspective | null) => {
      if (value) {
        onPerspectiveChange(value);
      }
    },
    [onPerspectiveChange],
  );

  return (
    <Toolbar>
      <ToolGroup
        value={perspective}
        orientation="vertical"
        exclusive
        onChange={changePerspective}
      >
        <FaroTooltip title="Top view" placement="right">
          <ToolButton
            aria-label="point clouds top view"
            value={Perspective.topView}
            selected={perspective === Perspective.topView}
          >
            <ViewPredefinedTopIcon />
          </ToolButton>
        </FaroTooltip>
        <FaroTooltip title="Front view" placement="right">
          <ToolButton
            aria-label="point clouds front view"
            value={Perspective.frontView}
            selected={perspective === Perspective.frontView}
          >
            <ViewPredefinedFrontIcon />
          </ToolButton>
        </FaroTooltip>
        <FaroTooltip title="Side view" placement="right">
          <ToolButton
            aria-label="point clouds side view"
            value={Perspective.sideView}
            selected={perspective === Perspective.sideView}
          >
            <ViewPredefinedRightIcon />
          </ToolButton>
        </FaroTooltip>
      </ToolGroup>
    </Toolbar>
  );
}
